import { z } from 'zod';
/**
 * Документы на плечо «терминал в РФ → получатель» (после растаможки).
 *
 * Два вида (kind): ORDER — поручение экспедитору (ФНС), ETRN — электронная
 * транспортная накладная. Заполняются по упрощённой форме на основе базового
 * поручения, сохраняются, по запросу отдают XML. Для ETRN опционально —
 * перевозчик/водитель/ТС.
 */
/**
 * Структурированная разбивка адреса (АдрРФ ЭТрН). Все поля опциональны —
 * заполняется вручную при создании ЭТрН и сохраняется для повторного
 * использования по тексту адреса.
 */
export const addressBreakdownSchema = z.object({
    index: z.string().max(10).optional(),
    regionCode: z.string().max(5).optional(),
    district: z.string().max(200).optional(),
    city: z.string().max(200).optional(),
    settlement: z.string().max(200).optional(),
    street: z.string().max(200).optional(),
    house: z.string().max(50).optional(),
    flat: z.string().max(50).optional(),
    /** Только для адреса доставки. */
    comment: z.string().max(500).optional(),
    lat: z.string().max(30).optional(),
    lon: z.string().max(30).optional(),
    /** Контактный телефон «для ЭПД», привязанный к адресу — идёт в сторону ЭТрН. */
    phone: z.string().max(50).optional(),
});
/**
 * Сохранение разбивки адреса из карточки стороны (клиент, привлекаемый исполнитель,
 * реквизиты экспедитора). Ключ — текст адреса, поэтому разбивка одна на адрес
 * и переиспользуется во всех документах, где этот адрес встречается.
 */
export const addressBreakdownSaveSchema = z.object({
    raw: z.string().min(1).max(500),
    parts: addressBreakdownSchema,
});
export const deliveryLegKindSchema = z.enum(['ORDER', 'ETRN']);
/**
 * Статус документа: CREATED — создан у нас; далее этапы обработки в ЭДО.МИГ24
 * (ЭПД). Промежуточные могут добавиться позже.
 */
export const deliveryLegStatusSchema = z.enum([
    'CREATED',
    'EDO_UPLOADED',
    'EDO_SIGNED',
    'EDO_SENT',
    'IN_TRANSIT',
    'DELIVERED',
]);
export const DELIVERY_LEG_STATUS_LABEL = {
    CREATED: 'Создан',
    EDO_UPLOADED: 'Загружен в ЭПД',
    EDO_SIGNED: 'Подписан',
    EDO_SENT: 'Отправлен',
    IN_TRANSIT: 'Груз в пути',
    DELIVERED: 'Груз доставлен',
};
/** Сторона документа (как в билдерах ФНС/ЭТрН). */
export const legPartySchema = z.object({
    type: z.string().nullable(),
    name: z.string().max(300),
    inn: z.string().max(20).nullable().optional(),
    kpp: z.string().max(20).nullable().optional(),
    ogrn: z.string().max(20).nullable().optional(),
    country: z.string().max(100).nullable().optional(),
    address: z.string().max(500).nullable().optional(),
    /**
     * Разбивка адреса стороны (АдрРФ). Не хранится в документе — подтягивается
     * по тексту адреса из справочника разбивок (заполняется в карточке
     * клиента / привлекаемого исполнителя / реквизитах экспедитора).
     */
    addressParts: addressBreakdownSchema.nullable().optional(),
    /** Телефон стороны (Контакт/Тлф ЭТрН — обязателен по XSD). */
    phone: z.string().max(50).nullable().optional(),
    /** Код абонента ЭДО.МИГ24 — для адресации электронных документов стороне. */
    edoSubscriberId: z.string().max(100).nullable().optional(),
});
/** Груз документа — выбранный из базового поручения, редактируемый. */
export const legCargoSchema = z.object({
    name: z.string().max(300),
    weightKg: z.number().nonnegative().optional(),
    weightNetKg: z.number().nonnegative().optional(),
    tareKg: z.number().nonnegative().optional(),
    volumeM3: z.number().nonnegative().optional(),
    units: z.number().int().nonnegative().optional(),
    marking: z.string().max(1000).optional(),
    originCountry: z.string().max(100).optional(),
    tnVed: z.string().max(20).optional(),
    isDangerous: z.boolean().default(false),
    /** Способ упаковки — описание (ЭТрН: СпУпак). */
    packaging: z.string().max(200).optional(),
    /** Код типа упаковки — классификатор ФТС (ЭТрН: ВидТар, напр. «AA»). */
    packagingCode: z.string().max(20).optional(),
    /** Номера контейнеров из базового поручения, попавшие в документ. */
    containers: z.array(z.string()).default([]),
});
/** Основание владения ТС (аренда/лизинг) — ОснАрЛиз. */
const ownershipDocSchema = z.object({
    docName: z.string().max(200).optional(),
    docNumber: z.string().max(100).optional(),
    docDate: z.string().max(20).optional(),
    ownerInn: z.string().max(20).optional(),
});
/** Транспортное средство / прицеп (СвТС/ТС + ПарТС + ОснАрЛиз). */
const vehicleSchema = z.object({
    stsNumber: z.string().max(30).optional(), // НомСТС
    vin: z.string().max(30).optional(), // НомерВИН
    regNumber: z.string().max(30).optional(), // РегНомер
    ownershipType: z.string().max(2).optional(), // ТипВлад (код)
    kind: z.string().max(100).optional(), // Тип (напр. «Седельный тягач»)
    brand: z.string().max(100).optional(), // Марка
    capacityTons: z.string().max(20).optional(), // Грузопод
    volume: z.string().max(20).optional(), // Вместим
    lease: ownershipDocSchema.optional(), // ОснАрЛиз
});
/**
 * Расширенные сведения ЭТрН (титул грузоотправителя) — всё опционально,
 * заполняется в форме ЭТрН на стадии перевозки. Имена полей соответствуют
 * элементам XML (см. etrn-title-go.builder).
 */
export const etrnDetailsSchema = z.object({
    /** Водитель (СвВодит): реквизиты ВУ, ИНН. ФИО/телефон — из основных полей. */
    driver: z
        .object({
        licenseNumber: z.string().max(30).optional(), // НомВУ
        licenseSeries: z.string().max(30).optional(), // СерВУ
        licenseDate: z.string().max(20).optional(), // ДатаВыдВУ
        inn: z.string().max(20).optional(), // ИННФЛ
    })
        .optional(),
    vehicle: vehicleSchema.optional(), // СвТС/ТС
    trailer: vehicleSchema.optional(), // Прицеп
    /** Договор перевозки/ТЭ (ДогУслПер). */
    contract: z
        .object({
        docName: z.string().max(200).optional(),
        docNumber: z.string().max(100).optional(),
        docDate: z.string().max(20).optional(),
    })
        .optional(),
    /** Указания ГО (УказГО): пломбы, запрет перегруза, нормы провоза. */
    seals: z.string().max(500).optional(), // СвПломба
    noReload: z.boolean().optional(), // ЗапрПерегруз
    transportNorms: z.string().max(500).optional(), // УкНормПрвз
    /** Опасный груз, детально (СвОпГруз). */
    dangerous: z
        .object({
        unNumber: z.string().max(10).optional(), // НомООН
        shippingName: z.string().max(300).optional(), // НадОтгНаим
        classId: z.string().max(10).optional(), // Клас
        classCode: z.string().max(20).optional(), // КласКод
        packingGroup: z.string().max(10).optional(), // ГрУп
        dangerSign: z.string().max(20).optional(), // ЗнОп
        tunnelCode: z.string().max(20).optional(), // КодОгрЧерТун
    })
        .optional(),
    /** Габариты грузового места (Габар), метры. */
    dimensions: z
        .object({
        height: z.string().max(20).optional(), // ВысЗнач
        length: z.string().max(20).optional(), // ДлЗнач
        width: z.string().max(20).optional(), // ШирЗнач
    })
        .optional(),
    /** Факт погрузки (СвПогруз). */
    loading: z
        .object({
        plannedAt: z.string().max(40).optional(), // ЗаявПогр (плановое время подачи)
        arrivalAt: z.string().max(40).optional(), // ФДатВрПриб
        departureAt: z.string().max(40).optional(), // ФДатВрУбыт
        massBrutto: z.string().max(20).optional(), // МасБрутОтгр
        massMethod: z.string().max(5).optional(), // МетОпрМасс (код)
        placesAccepted: z.string().max(10).optional(), // КолМестПрием
        /** Лицо, осуществляющее погрузку (совпадает с ГО?). */
        personSameAsShipper: z.boolean().optional(), // СовпГОП
        personPost: z.string().max(200).optional(), // РабЛицПогрГр/Должность
        personName: z.string().max(200).optional(), // ФИО
        /** Основание полномочий на погрузку (должностные обязанности) — ОДолжОб. */
        personBasis: z.string().max(255).optional(), // РабЛицПогрГр/ОДолжОб
        /** Владелец инфраструктуры места погрузки. */
        infraOwner: legPartySchema.nullable().optional(),
    })
        .optional(),
    /**
     * Блок 11 — отметки грузоотправителей/грузополучателей/перевозчиков (ОтметГО).
     * Каждая отметка: дата/время + описание обстоятельств; опц. реквизиты акта.
     */
    marks: z
        .array(z.object({
        dateTime: z.string().max(40).optional(), // ДатВрОтмет (ДД.ММ.ГГГГTчч:мм:сс+чч:мм)
        circumstances: z.string().max(2000).optional(), // ОбстОтмет
        // Опциональный акт (СвАкт): реквизиты документа + ИНН составителя.
        actName: z.string().max(200).optional(), // НаимДок
        actNumber: z.string().max(100).optional(), // НомерДок
        actDate: z.string().max(20).optional(), // ДатаДок
        actOwnerInn: z.string().max(20).optional(), // ИдРекСост/ИННЮЛ|ИННФЛ
    }))
        .optional(),
});
const baseFields = {
    customerParty: legPartySchema,
    expeditorParty: legPartySchema,
    shipperParty: legPartySchema,
    consigneeParty: legPartySchema,
    pickupAddress: z.string().min(1).max(500),
    pickupCountry: z.string().max(100).optional(),
    deliveryAddress: z.string().min(1).max(500),
    deliveryCountry: z.string().max(100).optional(),
    transportModes: z.array(z.string()).default(['AUTO']),
    /** Разбивка адресов (АдрРФ) — при создании ЭТрН, сохраняется для переиспользования. */
    pickupAddressParts: addressBreakdownSchema.optional(),
    deliveryAddressParts: addressBreakdownSchema.optional(),
    cargo: legCargoSchema,
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    comment: z.string().max(2000).optional(),
    /** Привлекаемый исполнитель — исполнитель по документу (заказчик — наш экспедитор). */
    performerCarrierId: z.string().nullable().optional(),
    // ETRN-only (опционально):
    carrierId: z.string().nullable().optional(),
    carrier: legPartySchema.nullable().optional(),
    driverName: z.string().max(200).optional(),
    driverPhone: z.string().max(50).optional(),
    vehicleRegNo: z.string().max(50).optional(),
    vehicleType: z.string().max(100).optional(),
    /** Грузоотправитель является экспедитором (ГОЭксп). По умолчанию да. */
    shipperIsExpeditor: z.boolean().optional(),
    /** Расширенные сведения ЭТрН (водитель/ТС/погрузка/договор/пломбы и т.п.). */
    etrnDetails: etrnDetailsSchema.optional(),
    /** Стоимость перевозки привлекаемому исполнителю (вносит менеджер). */
    price: z.number().nonnegative().optional(),
    /** Ставка НДС для стоимости: 'none' | '0' | '10' | '20'. */
    priceVatRate: z.string().max(10).optional(),
    priceCurrency: z.string().max(10).optional(),
};
export const deliveryLegCreateSchema = z.object({
    kind: deliveryLegKindSchema,
    /** Номер документа — если задан, используется вместо автогенерации. */
    number: z.string().max(50).optional(),
    /** Плечо маршрута поручения, на которое выпускается документ (опц., legacy). */
    orderSegmentId: z.string().optional(),
    /** Плечо перевозки, на которое выпускается документ (фаза 2). */
    shipmentLegId: z.string().optional(),
    ...baseFields,
});
/** Правка — всё опционально (kind не меняем). */
export const deliveryLegPatchSchema = z
    .object({
    customerParty: legPartySchema,
    expeditorParty: legPartySchema,
    shipperParty: legPartySchema,
    consigneeParty: legPartySchema,
    pickupAddress: z.string().min(1).max(500),
    pickupCountry: z.string().max(100).nullable(),
    deliveryAddress: z.string().min(1).max(500),
    deliveryCountry: z.string().max(100).nullable(),
    transportModes: z.array(z.string()),
    pickupAddressParts: addressBreakdownSchema.nullable(),
    deliveryAddressParts: addressBreakdownSchema.nullable(),
    cargo: legCargoSchema,
    dateFrom: z.string().nullable(),
    dateTo: z.string().nullable(),
    comment: z.string().max(2000).nullable(),
    performerCarrierId: z.string().nullable(),
    carrierId: z.string().nullable(),
    carrier: legPartySchema.nullable(),
    driverName: z.string().max(200).nullable(),
    driverPhone: z.string().max(50).nullable(),
    vehicleRegNo: z.string().max(50).nullable(),
    vehicleType: z.string().max(100).nullable(),
    number: z.string().max(50),
    shipperIsExpeditor: z.boolean().nullable(),
    etrnDetails: etrnDetailsSchema.nullable(),
    price: z.number().nonnegative().nullable(),
    priceVatRate: z.string().max(10).nullable(),
    priceCurrency: z.string().max(10).nullable(),
    status: deliveryLegStatusSchema,
})
    .partial();
export const deliveryLegDtoSchema = z.object({
    id: z.string(),
    /** Источник: перевозка (shipmentId) ИЛИ поручение (orderId + orderSegmentId). */
    shipmentId: z.string().nullable(),
    orderId: z.string().nullable(),
    orderSegmentId: z.string().nullable(),
    /** Плечо перевозки, на которое выпущен документ (фаза 2). */
    shipmentLegId: z.string().nullable(),
    /** Исполнитель — привлекаемый исполнитель, которому выдан документ. */
    performerCarrierId: z.string().nullable(),
    kind: deliveryLegKindSchema,
    status: deliveryLegStatusSchema,
    number: z.string(),
    customerParty: legPartySchema,
    expeditorParty: legPartySchema,
    shipperParty: legPartySchema,
    consigneeParty: legPartySchema,
    pickupAddress: z.string(),
    pickupCountry: z.string().nullable(),
    deliveryAddress: z.string(),
    deliveryCountry: z.string().nullable(),
    transportModes: z.array(z.string()),
    cargo: legCargoSchema,
    dateFrom: z.string().datetime().nullable(),
    dateTo: z.string().datetime().nullable(),
    comment: z.string().nullable(),
    carrierId: z.string().nullable(),
    carrierParty: legPartySchema.nullable(),
    driverName: z.string().nullable(),
    driverPhone: z.string().nullable(),
    vehicleRegNo: z.string().nullable(),
    vehicleType: z.string().nullable(),
    etrnDetails: etrnDetailsSchema.nullable(),
    shipperIsExpeditor: z.boolean(),
    price: z.number().nullable(),
    priceVatRate: z.string().nullable(),
    priceCurrency: z.string().nullable(),
    edoPackageId: z.string().nullable(),
    edoSignUrl: z.string().nullable(),
    edoStatus: z.string().nullable(),
    edoHasUpdate: z.boolean(),
    createdAt: z.string().datetime(),
});
/** Экспедиторский документ (Фаза 2): сохранённый XML с типом/направлением/статусом. */
export const expeditorDocumentDtoSchema = z.object({
    id: z.string(),
    type: z.enum(['POREXPKLT', 'POREXPEXP', 'POREXPOTZ', 'EXPRASP', 'SKLADRASP', 'ETRN_GO', 'ZAKZVGO']),
    direction: z.enum(['OUTGOING', 'INCOMING']),
    status: z.enum(['DRAFT', 'ISSUED', 'RECEIVED', 'ACCEPTED', 'SIGNED']),
    number: z.string().nullable(),
    fileName: z.string().nullable(),
    uid: z.string().nullable(),
    note: z.string().nullable(),
    createdAt: z.string(),
});
/** Тело запроса на приём входящей расписки: XML документа + заметка. */
export const receiveRaspiskaSchema = z.object({
    xml: z.string().min(1).max(2_000_000),
    note: z.string().max(1000).optional(),
});
/**
 * Дефолты для новой формы. Сервер отдаёт их по базовому поручению перевозки
 * либо по конкретному плечу маршрута поручения (тогда заполнены и pickup*,
 * transportModes и плановые даты плеча).
 */
export const deliveryLegDefaultsSchema = z.object({
    customerParty: legPartySchema,
    expeditorParty: legPartySchema,
    shipperParty: legPartySchema,
    consigneeParty: legPartySchema,
    /** Пункт погрузки — заполняется, когда дефолты строятся по плечу. */
    pickupAddress: z.string().default(''),
    pickupCountry: z.string().nullable().default(null),
    deliveryAddress: z.string(),
    deliveryCountry: z.string().nullable(),
    transportModes: z.array(z.string()).default(['AUTO']),
    dateFrom: z.string().nullable().default(null),
    dateTo: z.string().nullable().default(null),
    /**
     * Договор ТЭО перевозки (для предзаполнения ДогУслПер в блоке 1а ЭТрН). null —
     * договора ещё нет (проект). docDate — в формате ДД.ММ.ГГГГ.
     */
    etrnContract: z
        .object({ docName: z.string(), docNumber: z.string(), docDate: z.string() })
        .nullable()
        .default(null),
    /** Доступные для выбора грузы/контейнеры из базового поручения. */
    cargoOptions: z.array(legCargoSchema),
    /**
     * Отдельные контейнеры базового поручения — для режима «ЭТрН на один
     * контейнер». Каждый несёт снапшот груза (containers = только этот контейнер)
     * и сырой номер контейнера, из которого формируется номер ЭТрН.
     */
    containerUnits: z
        .array(z.object({
        id: z.string(),
        /** Сырой номер контейнера (напр. MSCU1234567); null — ещё не присвоен. */
        number: z.string().nullable(),
        type: z.string(),
        label: z.string(),
        cargo: legCargoSchema,
    }))
        .default([]),
});
const isBlank = (v) => v === undefined || v === null || String(v).trim() === '';
/**
 * Проверка обязательных полей ЭТрН по XSD ON_TRNACLGROT (титул ГО).
 * Возвращает список незаполненного обязательного, сгруппированный по секциям.
 * Автозаполняемые поля (номер/даты/версии/КНД) не проверяются.
 */
export function validateEtrnCompleteness(d) {
    const out = [];
    const add = (section, message) => out.push({ section, message });
    // Сторона: наименование, ИНН, телефон, адрес (Контакт/Тлф и Адрес обязательны).
    const checkParty = (section, p, label, addr = true) => {
        if (isBlank(p.name))
            add(section, `${label}: не указано наименование`);
        if (isBlank(p.inn))
            add(section, `${label}: не указан ИНН`);
        if (isBlank(p.phone))
            add(section, `${label}: не указан телефон`);
        if (addr && isBlank(p.address))
            add(section, `${label}: не указан адрес`);
    };
    checkParty('shipper', d.shipper, 'Грузоотправитель');
    checkParty('consignee', d.consignee, 'Грузополучатель');
    checkParty('carrier', d.carrier, 'Перевозчик (исполнитель)', false);
    checkParty('customer', d.customer, 'Заказчик');
    // Коды абонента ЭДО.МИГ24 — для адресации электронного документа сторонам.
    // Обязательны у трёх сторон: грузоотправитель, грузополучатель, перевозчик.
    if (isBlank(d.shipper.edoSubscriberId))
        add('shipper', 'Грузоотправитель: не указан код абонента ЭДО');
    if (isBlank(d.consignee.edoSubscriberId))
        add('consignee', 'Грузополучатель: не указан код абонента ЭДО');
    if (isBlank(d.carrier.edoSubscriberId))
        add('carrier', 'Перевозчик: не указан код абонента ЭДО');
    // Договор перевозки/ТЭ (ДогУслПер) — обязателен (внутри СвЗак).
    const ct = d.etrn.contract ?? {};
    if (isBlank(ct.docName) || isBlank(ct.docNumber) || isBlank(ct.docDate)) {
        add('customer', 'Договор перевозки/ТЭ: заполните наименование, номер и дату');
    }
    // Маршрут: пункты отправления и доставки.
    if (isBlank(d.pickupAddress))
        add('route', 'Не указан пункт отправления');
    if (isBlank(d.deliveryAddress))
        add('route', 'Не указан пункт доставки');
    // Груз: наименование, упаковка, кол-во мест, масса брутто.
    if (isBlank(d.cargo.name))
        add('cargo', 'Не указано наименование груза');
    if (isBlank(d.cargo.packaging))
        add('cargo', 'Не указан способ упаковки');
    // ВидТар — обязательный код классификатора ФТС (без валидного кода схема ЭТрН
    // бракует документ); выбирается из справочника «Вид упаковки (тары)».
    if (isBlank(d.cargo.packagingCode))
        add('cargo', 'Не указан вид упаковки (код тары)');
    if (isBlank(d.cargo.units))
        add('cargo', 'Не указано количество мест');
    if (isBlank(d.cargo.weightKg))
        add('cargo', 'Не указана масса брутто груза');
    // Опасный груз — все реквизиты СвОпГруз.
    if (d.cargo.isDangerous) {
        const g = d.etrn.dangerous ?? {};
        const dm = [
            ['unNumber', '№ ООН'],
            ['shippingName', 'отгрузочное наименование'],
            ['classId', 'класс'],
            ['classCode', 'классификационный код'],
            ['packingGroup', 'группа упаковки'],
            ['dangerSign', 'знак опасности'],
            ['tunnelCode', 'код ограничения через тоннели'],
        ];
        for (const [k, label] of dm)
            if (isBlank(g[k]))
                add('cargo', `Опасный груз: не указан(о) ${label}`);
    }
    // Транспорт: водитель (ФИО, телефон), ТС (гос.номер, тип владения, параметры).
    if (isBlank(d.driverName))
        add('transport', 'Не указан водитель (ФИО)');
    if (isBlank(d.driverPhone))
        add('transport', 'Не указан телефон водителя');
    const v = d.etrn.vehicle ?? {};
    if (isBlank(v.regNumber))
        add('transport', 'ТС: не указан гос. номер');
    if (isBlank(v.ownershipType))
        add('transport', 'ТС: не указан тип владения');
    if (isBlank(v.kind) || isBlank(v.brand) || isBlank(v.capacityTons) || isBlank(v.volume)) {
        add('transport', 'ТС: заполните параметры — тип, марку, грузоподъёмность, вместимость');
    }
    // Прицеп (Прицеп): если указан хотя бы один реквизит — обязательны гос.номер,
    // тип владения и все параметры ПарТС (Тип/Марка/Грузопод/Вместим required по XSD).
    const tr = d.etrn.trailer ?? {};
    const trailerPresent = [
        tr.stsNumber, tr.vin, tr.regNumber, tr.ownershipType,
        tr.kind, tr.brand, tr.capacityTons, tr.volume,
    ].some((x) => !isBlank(x));
    if (trailerPresent) {
        if (isBlank(tr.regNumber))
            add('transport', 'Прицеп: не указан гос. номер');
        if (isBlank(tr.ownershipType))
            add('transport', 'Прицеп: не указан тип владения');
        if (isBlank(tr.kind) || isBlank(tr.brand) || isBlank(tr.capacityTons) || isBlank(tr.volume)) {
            add('transport', 'Прицеп: заполните параметры — тип, марку, грузоподъёмность, вместимость');
        }
    }
    // Погрузка: даты/масса/метод/места (все атрибуты СвПогруз обязательны).
    const ld = d.etrn.loading ?? {};
    const lm = [
        ['plannedAt', 'плановое время подачи'],
        ['arrivalAt', 'дата/время прибытия'],
        ['departureAt', 'дата/время убытия'],
        ['massBrutto', 'масса брутто отгруженная'],
        ['massMethod', 'метод определения массы'],
        ['placesAccepted', 'количество мест при приёме'],
    ];
    for (const [k, label] of lm)
        if (isBlank(ld[k]))
            add('loading', `Погрузка: не указано ${label}`);
    // Работник, ответственный за погрузку (РабЛицПогрГр): если заполнено что-то
    // одно — обязательны и должность (@Должность), и ФИО (оба required по XSD).
    if (!isBlank(ld.personPost) || !isBlank(ld.personName)) {
        if (isBlank(ld.personPost))
            add('loading', 'Лицо погрузки: не указана должность');
        if (isBlank(ld.personName))
            add('loading', 'Лицо погрузки: не указано ФИО');
    }
    // Владелец инфраструктуры места погрузки (РекВладИнф, тип УчастникТрНТип):
    // если задан — обязательны наименование, ИНН и телефон (Контакт/Тлф required).
    const io = ld.infraOwner;
    if (io && (!isBlank(io.name) || !isBlank(io.inn) || !isBlank(io.phone))) {
        if (isBlank(io.name))
            add('loading', 'Владелец инфраструктуры: не указано наименование');
        if (isBlank(io.inn))
            add('loading', 'Владелец инфраструктуры: не указан ИНН');
        if (isBlank(io.phone))
            add('loading', 'Владелец инфраструктуры: не указан телефон');
    }
    return out;
}
/**
 * Проверка обязательных полей поручения экспедитору (POREXPKLT, титул клиента).
 * Возвращает список незаполненного обязательного, сгруппированный по секциям
 * (те же карточки формы, что и у ЭТрН: заказчик/исполнитель/ГО/ГП/маршрут/груз).
 * Автозаполняемое (номер/даты/версии/КНД/вид транспорта) не проверяется.
 */
export function validatePoruchenieCompleteness(d) {
    const out = [];
    const add = (section, message) => out.push({ section, message });
    // Стороны поручения: наименование и ИНН обязательны у всех четырёх.
    // customer — заказчик (наш экспедитор, СвКлнт); carrier — исполнитель (СвЭксп).
    const checkParty = (section, p, label) => {
        if (isBlank(p.name))
            add(section, `${label}: не указано наименование`);
        if (isBlank(p.inn))
            add(section, `${label}: не указан ИНН`);
    };
    checkParty('customer', d.customer, 'Заказчик (наш экспедитор)');
    checkParty('carrier', d.carrier, 'Исполнитель');
    checkParty('shipper', d.shipper, 'Грузоотправитель');
    checkParty('consignee', d.consignee, 'Грузополучатель');
    // Маршрут: пункты погрузки и выгрузки.
    if (isBlank(d.pickupAddress))
        add('route', 'Не указан пункт погрузки');
    if (isBlank(d.deliveryAddress))
        add('route', 'Не указан пункт выгрузки');
    // Груз: наименование и масса брутто (> 0).
    if (isBlank(d.cargo.name))
        add('cargo', 'Не указано наименование груза');
    if (d.cargo.weightKg == null || Number(d.cargo.weightKg) <= 0) {
        add('cargo', 'Не указана масса брутто груза');
    }
    return out;
}
//# sourceMappingURL=delivery-legs.js.map